import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from 'rxjs';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { DataGridColumnDef, GetUserProfileSearchByList } from './user-profile.constant';
import { UserProfile } from '@app/shared/classes/user-profile';
import { UserProfileEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/user-profile-edit-modal-dialog/user-profile-edit-modal-dialog.component';
import { CommonModalDialogComponent } from '@app/shared/components/common-modal-dialog/common-modal-dialog.component';
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from '@app/shared/components/common-modal-dialog/common-modal-dialog.constant';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { UserProfileDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/user-profile-data-grid-cell.component';
import { DMPRole } from '@app/shared/enums';
@Component({
  selector: 'app-user-profile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    DataGridComponent,
    CommonModule,
    SmartSearchFormComponent,
    MessageDataComponent,
    LoadingSpinnerComponent,
    UserProfileDataGridCell
  ],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.css'
})
export class UserProfileComponent extends PageBasedComponent {
  dataSource$: Observable<UserProfile[]>;
  dataGridColumnDef = DataGridColumnDef;
  selectedItems: UserProfile[] = [];
  formPath = NgxsFormPath.USER_PROFILE_SEARCH_FORM;
  userProfileSearchByList$: Observable<SearchByItem[]>;
  isSystemAdmin$: Observable<boolean>;

  @Select(AppDataState.currentUserProfileSearchBy) currentUserProfileSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_USER_PROFILE)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_USER_PROFILE)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_USER_PROFILE)) response$: Observable<WebApiResponse<UserProfile[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_USER_PROFILE)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_USER_PROFILE)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_USER_PROFILE;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentUserProfileSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.isSystemAdmin$ = this.profileMe$.pipe(map(profileMe => profileMe.role_id === DMPRole.SYSTEM_ADMIN));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    this.userProfileSearchByList$ = combineLatest([this.userProfileRole$.pipe(map(s => s.data)), this.currentTerminalDropDowns$, this.currentSubRegions$, this.isSystemAdmin$])
      .pipe(map((c) => GetUserProfileSearchByList(c)));
    this.actions$.pipe(ofActionSuccessful(AppDataAction.DeleteUserProfile), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));
  }

  rowSelected = (data: UserProfile[]) => {
    this.selectedItems = data ?? [];
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetUserProfile());
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetUserProfile(null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_USER_PROFILE;
    this.store.dispatch(new AppDataAction.GetUserProfile(info));
  }

  onCreate = () => {
    this.dialog.open(UserProfileEditModalDialogComponent, {
      width: '700px',
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true
    });
  }

  onEdit = () => {
    this.dialog.open(UserProfileEditModalDialogComponent, {
      width: '700px',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result.data !== ModalDialogResult.YES) {
        return;
      }
      this.store.dispatch(new AppDataAction.DeleteUserProfile(this.selectedItems.map(item => item.userProfile_Id)));
    })
  }
}
